import {useCallback, useRef, useState, useEffect, useMemo} from 'react'
import {type Node, type Edge} from 'reactflow'

/**
 * Interface for undo/redo stack items
 */
export interface UndoRedoStackItem {
  nodes: Node[]
  edges: Edge[]
  timestamp: number
  groupId?: string
}

const MAX_STACK_SIZE = 50

const DEBOUNCE_DELAY = 100

const STORAGE_KEY = 'flow-undo-redo-state'

function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func(...args), delay)
  }
}

/**
 * Custom hook for managing undo/redo functionality in React Flow
 *
 * This hook provides:
 * - Undo/redo stacks with a maximum size limit
 * - Methods to push new states, undo, and redo operations
 * - Prevention of infinite loops during undo/redo operations
 * - Proper state synchronization
 * - State persistence to localStorage
 * - Debounced state pushing for rapid changes
 * - State comparison to prevent unnecessary pushes
 * - Undo/redo groups for better UX
 *
 * @param initialNodes - Initial nodes state
 * @param initialEdges - Initial edges state
 * @param setNodes - Function to update nodes state
 * @param setEdges - Function to update edges state
 * @returns Object with undo/redo methods and state
 */
export const useUndoRedo = (
  initialNodes: Node[],
  initialEdges: Edge[],
  setNodes: (nodes: Node[]) => void,
  setEdges: (edges: Edge[]) => void
) => {
  const [undoStack, setUndoStack] = useState<UndoRedoStackItem[]>([])

  const [redoStack, setRedoStack] = useState<UndoRedoStackItem[]>([])

  const isUndoRedoOperation = useRef(false)

  const currentStateRef = useRef<{nodes: Node[]; edges: Edge[]}>({nodes: [], edges: []})

  const currentGroupId = useRef<string | undefined>(undefined)

  useEffect(() => {
    try {
      const savedState = localStorage.getItem(STORAGE_KEY)
      if (savedState) {
        const parsed = JSON.parse(savedState)
        if (parsed.undoStack && parsed.redoStack) {
          setUndoStack(parsed.undoStack)
          setRedoStack(parsed.redoStack)
        }
      }
    } catch (error) {
      console.warn('Failed to load undo/redo state from localStorage:', error)
    }
  }, [])

  useEffect(() => {
    try {
      const stateToSave = {
        undoStack,
        redoStack,
        timestamp: Date.now(),
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave))
    } catch (error) {
      console.warn('Failed to save undo/redo state to localStorage:', error)
    }
  }, [undoStack, redoStack])

  useEffect(() => {
    if (initialNodes.length > 0 || initialEdges.length > 0) {
      const initialState: UndoRedoStackItem = {
        nodes: JSON.parse(JSON.stringify(initialNodes)),
        edges: JSON.parse(JSON.stringify(initialEdges)),
        timestamp: Date.now(),
      }
      setUndoStack([initialState])
      currentStateRef.current = {nodes: initialNodes, edges: initialEdges}
    }
  }, [])

  const createDeepCopy = useCallback((nodes: Node[], edges: Edge[]): UndoRedoStackItem => {
    try {
      return {
        nodes: JSON.parse(JSON.stringify(nodes)),
        edges: JSON.parse(JSON.stringify(edges)),
        timestamp: Date.now(),
        groupId: currentGroupId.current,
      }
    } catch (error) {
      console.error('Error creating deep copy:', error)
      return {
        nodes: [...nodes],
        edges: [...edges],
        timestamp: Date.now(),
        groupId: currentGroupId.current,
      }
    }
  }, [])

  const hasStateChanged = useCallback((newNodes: Node[], newEdges: Edge[]) => {
    const current = currentStateRef.current
    try {
      return (
        JSON.stringify(newNodes) !== JSON.stringify(current.nodes) ||
        JSON.stringify(newEdges) !== JSON.stringify(current.edges)
      )
    } catch (error) {
      console.warn('Error comparing states:', error)
      return true
    }
  }, [])

  const startGroup = useCallback(() => {
    currentGroupId.current = `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }, [])

  /**
   * End the current undo/redo group
   */
  const endGroup = useCallback(() => {
    currentGroupId.current = undefined
  }, [])

  /**
   * Push the current state to the undo stack
   * This should be called before any operation that modifies nodes/edges
   *
   * @param currentNodes - Current nodes state
   * @param currentEdges - Current edges state
   */
  const pushToUndoStack = useCallback(
    (currentNodes: Node[], currentEdges: Edge[]) => {
      if (isUndoRedoOperation.current) return

      if (!Array.isArray(currentNodes) || !Array.isArray(currentEdges)) {
        console.warn('Invalid input for pushToUndoStack: nodes and edges must be arrays')
        return
      }

      // Check if state has actually changed
      if (!hasStateChanged(currentNodes, currentEdges)) {
        return
      }

      // Create a deep copy of the current state
      const currentState = createDeepCopy(currentNodes, currentEdges)

      setUndoStack((stack) => {
        const newStack = [...stack, currentState]
        // Limit stack size to prevent memory issues
        if (newStack.length > MAX_STACK_SIZE) {
          return newStack.slice(-MAX_STACK_SIZE)
        }
        return newStack
      })

      currentStateRef.current = {nodes: currentNodes, edges: currentEdges}

      setRedoStack([])
    },
    [createDeepCopy, hasStateChanged]
  )

  const debouncedPushToUndoStack = useMemo(
    () =>
      debounce((nodes: Node[], edges: Edge[]) => {
        pushToUndoStack(nodes, edges)
      }, DEBOUNCE_DELAY),
    [pushToUndoStack]
  )

  /**
   * Undo the last operation
   * Moves the current state to redo stack and restores the previous state
   *
   * @param currentNodes - Current nodes state
   * @param currentEdges - Current edges state
   */
  const undo = useCallback(
    (currentNodes: Node[], currentEdges: Edge[]) => {
      if (undoStack.length === 0) return

      // Validate input
      if (!Array.isArray(currentNodes) || !Array.isArray(currentEdges)) {
        console.warn('Invalid input for undo: nodes and edges must be arrays')
        return
      }

      // Set flag to prevent infinite loops
      isUndoRedoOperation.current = true

      setUndoStack((stack) => {
        if (stack.length === 0) return stack

        // Get the previous state
        const previousState = stack[stack.length - 1]

        // Save current state to redo stack
        const currentState = createDeepCopy(currentNodes, currentEdges)

        setRedoStack((redoStack) => [...redoStack, currentState])

        // Restore previous state
        setNodes(previousState.nodes)
        setEdges(previousState.edges)

        // Update current state ref
        currentStateRef.current = {nodes: previousState.nodes, edges: previousState.edges}

        // Reset flag after a brief delay to allow state updates to complete
        setTimeout(() => {
          isUndoRedoOperation.current = false
        }, 100)

        // Remove the restored state from undo stack
        return stack.slice(0, -1)
      })
    },
    [undoStack.length, setNodes, setEdges, createDeepCopy]
  )

  /**
   * Redo the last undone operation
   * Moves the current state to undo stack and restores the next state
   *
   * @param currentNodes - Current nodes state
   * @param currentEdges - Current edges state
   */
  const redo = useCallback(
    (currentNodes: Node[], currentEdges: Edge[]) => {
      if (redoStack.length === 0) return

      // Validate input
      if (!Array.isArray(currentNodes) || !Array.isArray(currentEdges)) {
        console.warn('Invalid input for redo: nodes and edges must be arrays')
        return
      }

      // Set flag to prevent infinite loops
      isUndoRedoOperation.current = true

      setRedoStack((stack) => {
        if (stack.length === 0) return stack

        // Get the next state
        const nextState = stack[stack.length - 1]

        // Save current state to undo stack
        const currentState = createDeepCopy(currentNodes, currentEdges)

        setUndoStack((undoStack) => [...undoStack, currentState])

        // Restore next state
        setNodes(nextState.nodes)
        setEdges(nextState.edges)

        // Update current state ref
        currentStateRef.current = {nodes: nextState.nodes, edges: nextState.edges}

        // Reset flag after a brief delay to allow state updates to complete
        setTimeout(() => {
          isUndoRedoOperation.current = false
        }, 100)

        // Remove the restored state from redo stack
        return stack.slice(0, -1)
      })
    },
    [redoStack.length, setNodes, setEdges, createDeepCopy]
  )

  /**
   * Clear both undo and redo stacks
   * Useful when starting a new flow or resetting the editor
   */
  const clearHistory = useCallback(() => {
    setUndoStack([])
    setRedoStack([])
    currentStateRef.current = {nodes: [], edges: []}
    currentGroupId.current = undefined
  }, [])

  /**
   * Initialize the undo stack with current state
   * Useful when loading a saved flow
   */
  const initializeHistory = useCallback(
    (nodes: Node[], edges: Edge[]) => {
      if (!Array.isArray(nodes) || !Array.isArray(edges)) {
        console.warn('Invalid input for initializeHistory: nodes and edges must be arrays')
        return
      }

      const initialState = createDeepCopy(nodes, edges)
      setUndoStack([initialState])
      setRedoStack([])
      currentStateRef.current = {nodes, edges}
      currentGroupId.current = undefined
    },
    [createDeepCopy]
  )

  const getStats = useCallback(() => {
    return {
      undoCount: undoStack.length,
      redoCount: redoStack.length,
      totalOperations: undoStack.length + redoStack.length,
      lastOperation: undoStack.length > 0 ? undoStack[undoStack.length - 1].timestamp : null,
    }
  }, [undoStack, redoStack])

  const canUndo = undoStack.length > 0

  const canRedo = redoStack.length > 0

  return {
    pushToUndoStack,
    debouncedPushToUndoStack,
    undo,
    redo,
    clearHistory,
    initializeHistory,
    startGroup,
    endGroup,
    getStats,

    canUndo,
    canRedo,
    undoStackLength: undoStack.length,
    redoStackLength: redoStack.length,
    isUndoRedoOperation,
  }
}

export type UndoRedoHook = ReturnType<typeof useUndoRedo>
